import React, { useState, useEffect, useCallback } from "react";
import { RefreshCw } from "lucide-react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { Field, SelectShell, inputStyles, selectStyles } from "../../components/crm/formControls";
import { getJudgementCaptcha, searchJudgements } from "../../services/ngtApi";

import SelectMenu from "../../components/crm/SelectMenu";
/**
 * NGT "Judgement / Orders" search.
 *
 * Rendered inside <NGTSearch /> under the "Judgement / Orders" main tab, so it
 * intentionally renders only the inner card (no Navbar / background — the parent
 * provides those). Four search methods share one captcha session:
 *   Date Range | Case Number | Party Name | Judge
 *
 * On a successful search it navigates to /portal/ngt/judgementList with the
 * results (mirrors the Case-Status flow, which opens results on a new page).
 */

const JUDGEMENT_TABS = [
  { id: "date", label: "Date Range" },
  { id: "case-number", label: "Case Number" },
  { id: "party-name", label: "Party Name" },
  { id: "judge", label: "Judge" },
];

// "Search By" (order_by) — mirrors the NGT judgement/order form.
const ORDER_BY_OPTIONS = [
  { value: "1", label: "Order Date" },
  { value: "2", label: "Uploading Date" },
  { value: "3", label: "Both" },
];

// Party type ids used by the NGT judgement/order party-name search.
const PARTY_TYPE_OPTIONS = [
  { value: "1", label: "Applicant / Petitioner" },
  { value: "2", label: "Respondent" },
];

const SEARCH_TIPS = [
  "Select a Zonal Bench to search",
  "Captcha is case sensitive",
  "Refresh the captcha if it's unclear",
];

// HTML <input type="date"> yields yyyy-mm-dd; the NGT backend expects dd/mm/yyyy.
const toDdMmYyyy = (isoDate) => {
  if (!isoDate) return "";
  const [y, m, d] = isoDate.split("-");
  if (!y || !m || !d) return isoDate;
  return `${d}/${m}/${y}`;
};

function NGTJudgementSearch() {
  const navigate = useNavigate();

  // UI state
  const [activeTab, setActiveTab] = useState("date");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Captcha + zonal benches (shared across all judgement search methods)
  const [zones, setZones] = useState([]);
  const [captchaImage, setCaptchaImage] = useState("");
  const [captchaLoading, setCaptchaLoading] = useState(false);
  const [cookies, setCookies] = useState(null);

  // Year options — NGT was constituted in 2010
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: currentYear - 2009 }, (_, i) => String(currentYear - i));

  // Separate form state per search method ("searchType" = the Search By dropdown)
  const [dateForm, setDateForm] = useState({ zone: "", searchType: "1", fromDate: "", toDate: "", captcha: "" });
  const [caseForm, setCaseForm] = useState({ zone: "", searchType: "1", caseType: "1", caseNo: "", year: "", captcha: "" });
  const [partyForm, setPartyForm] = useState({ zone: "", searchType: "1", partyType: "", partyName: "", captcha: "" });
  const [judgeForm, setJudgeForm] = useState({ zone: "", searchType: "1", judge: "", fromDate: "", toDate: "", captcha: "" });

  // Fetch a fresh captcha + zone list
  const loadCaptcha = useCallback(async () => {
    setCaptchaLoading(true);
    setError("");
    setCaptchaImage("");
    try {
      const data = await getJudgementCaptcha();
      if (data?.success === false) {
        setError(data.error || "Failed to load captcha. Please try again.");
        return;
      }
      setZones(Array.isArray(data?.zones) ? data.zones : []);
      setCaptchaImage(data?.captchaImage || "");
      setCookies(data?.cookies || null);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || "Failed to load captcha");
    } finally {
      setCaptchaLoading(false);
    }
  }, []);

  // Reload captcha whenever the search method changes
  useEffect(() => {
    loadCaptcha();
    setError("");
  }, [activeTab, loadCaptcha]);

  const handleRefreshCaptcha = () => loadCaptcha();

  // Build the request body for the active search method
  const buildParams = () => {
    switch (activeTab) {
      case "date":
        return {
          zone_type: dateForm.zone,
          from_date: toDdMmYyyy(dateForm.fromDate),
          to_date: toDdMmYyyy(dateForm.toDate),
          search_type: dateForm.searchType,
          captcha_input: dateForm.captcha,
          cookies,
        };
      case "case-number":
        return {
          zone_type: caseForm.zone,
          case_type: caseForm.caseType,
          case_no: caseForm.caseNo,
          case_year: caseForm.year,
          search_type: caseForm.searchType,
          captcha_input: caseForm.captcha,
          cookies,
        };
      case "party-name":
        return {
          zone_type: partyForm.zone,
          party_type: partyForm.partyType,
          party_name: partyForm.partyName,
          search_type: partyForm.searchType,
          captcha_input: partyForm.captcha,
          cookies,
        };
      case "judge":
        return {
          zone_type: judgeForm.zone,
          judge_name: judgeForm.judge,
          from_date: toDdMmYyyy(judgeForm.fromDate),
          to_date: toDdMmYyyy(judgeForm.toDate),
          search_type: judgeForm.searchType,
          captcha_input: judgeForm.captcha,
          cookies,
        };
      default:
        return null;
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const params = buildParams();
      if (!params) return;

      const data = await searchJudgements(activeTab, params);

      if (data?.success === false) {
        const msg = data.error || "Search failed. Please try again.";
        setError(msg);
        toast.error(msg, { duration: 5000 });
        loadCaptcha();
        return;
      }

      const judgements = Array.isArray(data?.judgements) ? data.judgements : [];
      if (judgements.length === 0) {
        const msg = "No judgements / orders found for the given criteria.";
        setError(msg);
        toast.error(msg, { duration: 5000 });
        loadCaptcha();
        return;
      }

      navigate("/portal/ngt/judgementList", {
        state: {
          judgements,
          searchType: activeTab,
          searchParams: params,
          cookies,
        },
      });
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || "Failed to search judgements/orders";
      setError(msg);
      toast.error(msg, { duration: 5000 });
      loadCaptcha();
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    switch (activeTab) {
      case "date":
        setDateForm({ zone: "", searchType: "1", fromDate: "", toDate: "", captcha: "" });
        break;
      case "case-number":
        setCaseForm({ zone: "", searchType: "1", caseType: "1", caseNo: "", year: "", captcha: "" });
        break;
      case "party-name":
        setPartyForm({ zone: "", searchType: "1", partyType: "", partyName: "", captcha: "" });
        break;
      case "judge":
        setJudgeForm({ zone: "", searchType: "1", judge: "", fromDate: "", toDate: "", captcha: "" });
        break;
      default:
        break;
    }
    loadCaptcha();
  };

  // Reusable Zonal Bench dropdown
  const renderZoneField = (value, onChange) => (
    <Field label="Zonal Bench">
      <SelectShell busy={captchaLoading}>
        <SelectMenu chevron={false} value={value} onChange={onChange} className={selectStyles} required>
          <option value="" disabled>
            {captchaLoading ? "Loading benches..." : "Select Bench"}
          </option>
          {zones.map((z) => (
            <option key={z.value} value={z.value}>
              {z.label}
            </option>
          ))}
        </SelectMenu>
      </SelectShell>
    </Field>
  );

  // Reusable "Search By" (order_by) dropdown
  const renderSearchByField = (value, onChange) => (
    <Field label="Search By">
      <SelectShell>
        <SelectMenu chevron={false} value={value} onChange={onChange} className={selectStyles} required>
          {ORDER_BY_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </SelectMenu>
      </SelectShell>
    </Field>
  );

  const renderLeftFields = () => {
    switch (activeTab) {
      case "date":
        return (
          <>
            {renderZoneField(dateForm.zone, (e) => setDateForm({ ...dateForm, zone: e.target.value }))}
            <Field label="From Date">
              <input
                type="date"
                value={dateForm.fromDate}
                onChange={(e) => setDateForm({ ...dateForm, fromDate: e.target.value })}
                className={inputStyles}
                required
              />
            </Field>
            <Field label="To Date">
              <input
                type="date"
                value={dateForm.toDate}
                onChange={(e) => setDateForm({ ...dateForm, toDate: e.target.value })}
                className={inputStyles}
                required
              />
            </Field>
            {renderSearchByField(dateForm.searchType, (e) => setDateForm({ ...dateForm, searchType: e.target.value }))}
          </>
        );
      case "case-number":
        return (
          <>
            {renderZoneField(caseForm.zone, (e) => setCaseForm({ ...caseForm, zone: e.target.value }))}
            <Field label="Case Type">
              <input
                type="text"
                value={caseForm.caseType}
                onChange={(e) => setCaseForm({ ...caseForm, caseType: e.target.value })}
                placeholder="Case Type (e.g. 1)"
                className={inputStyles}
                required
              />
            </Field>
            <Field label="Case Number">
              <input
                type="text"
                value={caseForm.caseNo}
                onChange={(e) => setCaseForm({ ...caseForm, caseNo: e.target.value })}
                placeholder="Case Number"
                className={inputStyles}
                required
              />
            </Field>
            <Field label="Case Year" required={false}>
              <SelectShell>
                <SelectMenu chevron={false}
                  value={caseForm.year}
                  onChange={(e) => setCaseForm({ ...caseForm, year: e.target.value })}
                  className={selectStyles}
                >
                  <option value="">Select Year (optional)</option>
                  {yearOptions.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </SelectMenu>
              </SelectShell>
            </Field>
            {renderSearchByField(caseForm.searchType, (e) => setCaseForm({ ...caseForm, searchType: e.target.value }))}
          </>
        );
      case "party-name":
        return (
          <>
            {renderZoneField(partyForm.zone, (e) => setPartyForm({ ...partyForm, zone: e.target.value }))}
            <Field label="Party Type">
              <SelectShell>
                <SelectMenu chevron={false}
                  value={partyForm.partyType}
                  onChange={(e) => setPartyForm({ ...partyForm, partyType: e.target.value })}
                  className={selectStyles}
                  required
                >
                  <option value="" disabled>Select Party Type</option>
                  {PARTY_TYPE_OPTIONS.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </SelectMenu>
              </SelectShell>
            </Field>
            <Field label="Party Name">
              <input
                type="text"
                value={partyForm.partyName}
                onChange={(e) => setPartyForm({ ...partyForm, partyName: e.target.value })}
                placeholder="Party Name"
                className={inputStyles}
                required
              />
            </Field>
            {renderSearchByField(partyForm.searchType, (e) => setPartyForm({ ...partyForm, searchType: e.target.value }))}
          </>
        );
      case "judge":
        return (
          <>
            {renderZoneField(judgeForm.zone, (e) => setJudgeForm({ ...judgeForm, zone: e.target.value }))}
            <Field label="Judge Name">
              <input
                type="text"
                value={judgeForm.judge}
                onChange={(e) => setJudgeForm({ ...judgeForm, judge: e.target.value })}
                placeholder="Judge name or ID"
                className={inputStyles}
                required
              />
              <p className="mt-1 text-[11px] text-slate-500">
                Enter a judge name (e.g. "Senthil") or numeric ID — names are matched automatically.
              </p>
            </Field>
            <Field label="From Date">
              <input
                type="date"
                value={judgeForm.fromDate}
                onChange={(e) => setJudgeForm({ ...judgeForm, fromDate: e.target.value })}
                className={inputStyles}
                required
              />
            </Field>
            <Field label="To Date">
              <input
                type="date"
                value={judgeForm.toDate}
                onChange={(e) => setJudgeForm({ ...judgeForm, toDate: e.target.value })}
                className={inputStyles}
                required
              />
            </Field>
            {renderSearchByField(judgeForm.searchType, (e) => setJudgeForm({ ...judgeForm, searchType: e.target.value }))}
          </>
        );
      default:
        return null;
    }
  };

  // Captcha value + setter for the active method
  const getCaptchaProps = () => {
    switch (activeTab) {
      case "date":
        return { value: dateForm.captcha, onChange: (e) => setDateForm({ ...dateForm, captcha: e.target.value }) };
      case "case-number":
        return { value: caseForm.captcha, onChange: (e) => setCaseForm({ ...caseForm, captcha: e.target.value }) };
      case "party-name":
        return { value: partyForm.captcha, onChange: (e) => setPartyForm({ ...partyForm, captcha: e.target.value }) };
      case "judge":
        return { value: judgeForm.captcha, onChange: (e) => setJudgeForm({ ...judgeForm, captcha: e.target.value }) };
      default:
        return { value: "", onChange: () => {} };
    }
  };

  const activeTabLabel = JUDGEMENT_TABS.find((t) => t.id === activeTab)?.label || "";
  const captchaProps = getCaptchaProps();

  return (
    <>
      {/* Full-screen overlay while a search is in flight */}
      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl px-8 py-7 shadow-2xl text-center max-w-sm w-full mx-4">
            <div className="flex justify-center mb-4">
              <div className="w-12 h-12 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-1">Searching…</h3>
            <p className="text-sm text-gray-600">Please wait a moment.</p>
          </div>
        </div>
      )}

      {/* Search card — full width (rendered inside NGTSearch's main) */}
      <div className="mt-4 w-full overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <form onSubmit={handleSearch}>
          <div className="p-5 sm:p-6">
            {/* Search By pills */}
            <p className="mb-2 text-center text-xs font-medium text-slate-600">Search By</p>
            <div className="flex flex-wrap justify-center gap-2">
              {JUDGEMENT_TABS.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`rounded-full px-4 py-1.5 text-[13px] font-medium transition-all duration-200 ${
                    activeTab === tab.id
                      ? "bg-yellow-500 text-slate-900 shadow-sm"
                      : "border border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <h2 className="mt-6 text-sm font-semibold text-slate-800">
              NGT : Search by {activeTabLabel}
            </h2>

            {error && (
              <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600 ring-1 ring-red-100">{error}</p>
            )}

            {/* Fields + captcha share one grid so they align across the full width */}
            <div className="mt-4 grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-4">
              {renderLeftFields()}

              <Field label="Captcha" className="lg:col-start-1">
                <div className="flex items-stretch gap-2">
                  <div className="flex h-10 flex-1 items-center justify-center rounded-lg border border-slate-300 bg-slate-50">
                    {captchaLoading ? (
                      <span className="flex items-center gap-2 text-sm text-slate-500">
                        <RefreshCw className="h-4 w-4 animate-spin" />
                        Loading...
                      </span>
                    ) : captchaImage ? (
                      <img
                        src={captchaImage}
                        alt="CAPTCHA"
                        className="max-h-[34px] object-contain"
                        onError={() => setError("Failed to load captcha image.")}
                      />
                    ) : (
                      <span className="px-2 text-center text-xs text-slate-400">Captcha unavailable</span>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={handleRefreshCaptcha}
                    disabled={captchaLoading}
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-slate-300 bg-white text-slate-500 transition-colors hover:border-yellow-400 hover:text-yellow-600 disabled:opacity-50"
                    title="Refresh Captcha"
                  >
                    <RefreshCw className="h-4 w-4" />
                  </button>
                </div>
              </Field>

              <Field label="Enter Captcha">
                <input
                  type="text"
                  value={captchaProps.value}
                  onChange={captchaProps.onChange}
                  placeholder="Enter Captcha"
                  className={inputStyles}
                  required
                />
              </Field>
            </div>
          </div>

          {/* Footer — tips + actions */}
          <div className="flex flex-col gap-4 border-t border-slate-100 bg-slate-50/60 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
            <ul className="flex flex-wrap items-center gap-x-5 gap-y-1">
              {SEARCH_TIPS.map((tip) => (
                <li key={tip} className="flex items-center gap-1.5 text-xs text-slate-500">
                  <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-yellow-400" />
                  {tip}
                </li>
              ))}
            </ul>
            <div className="flex shrink-0 gap-3">
              <button
                type="button"
                onClick={handleReset}
                className="h-10 rounded-lg border border-slate-300 bg-white px-6 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-60"
                disabled={loading}
              >
                Reset
              </button>
              <button
                type="submit"
                className="h-10 rounded-lg bg-yellow-500 px-8 text-sm font-semibold text-slate-900 transition-colors hover:bg-yellow-400 disabled:cursor-not-allowed disabled:opacity-60"
                disabled={loading || captchaLoading || !captchaImage}
              >
                {loading ? "Searching..." : "Search Orders"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </>
  );
}

export default NGTJudgementSearch;
